import React, { useState, useMemo, lazy, Suspense, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { Package, AlertCircle, Search, Plus } from 'lucide-react';
import { DEFAULT_USD_GEL_RATE } from './lib/constants';
import { Card, CardHeader, CardTitle, CardContent, Input, Select, Skeleton, Tabs } from './components/ui';
import { useItems, useAccounts, useAccountLimits, useDashboardStats, useDebouncedSearch, usePaymentStats, useUpdateItem, useDeleteItem } from './hooks';
import { useShippingCompanies, useSourceCompanies } from './hooks';
import { useToast } from './components/Toast';
import AuthWrapper, { AuthLoadingSkeleton } from './components/AuthWrapper';
import DashboardStats from './components/dashboard/DashboardStats';
import AccountLimits from './components/dashboard/AccountLimits';
import AnalyticsSummary from './components/dashboard/AnalyticsSummary';
import Header from './components/layout/Header';
import BulkActionsToolbar from './components/BulkActionsToolbar';
import AddPaymentForm from './components/payments/AddPaymentForm';
import { translateStatus, formatDate, formatCurrency } from './lib/utils';
import BottomNav from './components/navigation/BottomNav';
import FloatingActionButton from './components/navigation/FloatingActionButton';
import ItemCard from './components/items/ItemCard';
import AccountsManager from './components/accounts/AccountsManager';

const ItemsTable = lazy(() => import('./components/items/ItemsTable'));
const AddItemForm = lazy(() => import('./components/forms/AddItemForm'));
const CompaniesManager = lazy(() => import('./components/companies/CompaniesManager'));
const ClientsManager = lazy(() => import('./components/clients/ClientsManager'));

const tabs = [
  { id: 'items', label: 'ნივთები' },
  { id: 'clients', label: 'კლიენტები' },
  { id: 'companies', label: 'კომპანიები' },
  { id: 'accounts', label: 'აქაუნთები' }
];

const ItemsTableSkeleton = () => (
  <Card>
    <CardContent className="pt-6">
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    </CardContent>
  </Card>
);

const AddItemFormSkeleton = () => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <Skeleton className="h-[600px] w-[500px]" />
  </div>
);

const EmptyItemState = ({ onAddItem }) => (
  <div className="text-center py-16 px-4">
    <div className="relative inline-block mb-6">
      <div className="absolute inset-0 bg-pink-200/50 rounded-full blur-3xl animate-pulse" style={{ width: '140px', height: '140px', margin: '0 auto' }} />
      <div className="relative text-7xl mb-2 animate-bounce-subtle">🛍️</div>
    </div>
    <h3 className="text-xl font-bold text-foreground mb-2">ჯერ ნივთები არ დაგიმატებია</h3>
    <p className="text-muted-foreground mb-8 max-w-xs mx-auto">დააჭირე + ღილაკს ნივთის დასამატებლად</p>
    <button
      onClick={onAddItem}
      className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-pink-500 to-rose-500 text-white hover:from-pink-600 hover:to-rose-600 h-14 px-8 font-semibold shadow-lg shadow-pink-500/30 hover:shadow-xl hover:shadow-pink-500/40 transform hover:scale-105 active:scale-95 transition-all duration-200"
    >
      <Plus className="w-5 h-5 mr-2" />
      ნივთის დამატება
    </button>
  </div>
);

export default function App() {
  const toast = useToast();
  const { items: allItems, isLoading: itemsLoading } = useItems();
  const { data: accounts = [] } = useAccounts();
  const { data: accountLimits = [] } = useAccountLimits();
  const { data: stats } = useDashboardStats();
  const { data: paymentStats } = usePaymentStats();
  const { data: shippingCompanies = [] } = useShippingCompanies();
  const { data: sourceCompanies = [] } = useSourceCompanies();
  const updateItemMutation = useUpdateItem();
  const deleteItemMutation = useDeleteItem();

  const [activeTab, setActiveTab] = useState('items');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [quickPayItem, setQuickPayItem] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebouncedSearch('', 300);
  
  // Bulk selection state
  const [selectedItems, setSelectedItems] = useState(new Set());

  const handleNextStatus = async (item) => {
    const statusFlow = {
      ordered: 'at_carrier',
      at_carrier: 'shipped',
      shipped: 'arrived',
      arrived: 'at_home',
      at_home: 'delivered_to_client',
      delivered_to_client: 'delivered_to_client'
    };
    const nextStatus = statusFlow[item.status];
    if (nextStatus && nextStatus !== item.status) {
      await updateItemMutation.mutateAsync({ id: item.id, data: { status: nextStatus } });
    }
  };

  const [editItem, setEditItem] = useState(null);

  const handleEditItem = (item) => {
    setEditItem(item);
    setIsAddDialogOpen(true);
  };

  const filteredItems = useMemo(() => {
    if (!allItems) return [];
    if (!debouncedSearchTerm && statusFilter === 'all' && platformFilter === 'all') return allItems;

    const searchLower = debouncedSearchTerm.toLowerCase();
    return allItems.filter(item => {
      const matchesSearch = !debouncedSearchTerm ||
        item.item_number?.toLowerCase().includes(searchLower) ||
        item.name?.toLowerCase().includes(searchLower) ||
        item.tracking_code?.toLowerCase().includes(searchLower) ||
        item.client_name?.toLowerCase().includes(searchLower);

      return matchesSearch &&
        (statusFilter === 'all' || item.status === statusFilter) &&
        (platformFilter === 'all' || item.platform === platformFilter);
    });
  }, [allItems, debouncedSearchTerm, statusFilter, platformFilter]);

  const platforms = useMemo(() => {
    if (!allItems) return [];
    return [...new Set(allItems.map(item => item.platform))];
  }, [allItems]);

  // Bulk selection handlers
  const handleSelectItem = useCallback((itemId, isSelected) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(itemId);
      } else {
        newSet.delete(itemId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback((isSelected) => {
    if (isSelected) {
      setSelectedItems(new Set(filteredItems.map(item => item.id)));
    } else {
      setSelectedItems(new Set());
    }
  }, [filteredItems]);

  const handleClearSelection = useCallback(() => {
    setSelectedItems(new Set());
  }, []);

  const handleBulkDelete = useCallback(async () => {
    const ids = Array.from(selectedItems);
    for (const id of ids) {
      await deleteItemMutation.mutateAsync(id);
    }
    toast.success(`${ids.length} ნივთი წაიშალა!`);
    setSelectedItems(new Set());
  }, [selectedItems, deleteItemMutation, toast]);

  const handleBulkStatusChange = useCallback(async (newStatus) => {
    const ids = Array.from(selectedItems);
    for (const id of ids) {
      await updateItemMutation.mutateAsync({ id, data: { status: newStatus } });
    }
    toast.success(`${ids.length} ნივთის სტატუსი შეიცვალა!`);
    setSelectedItems(new Set());
  }, [selectedItems, updateItemMutation, toast]);

  const exportToPdf = useCallback(() => {
    const totalProfit = filteredItems.reduce((sum, item) => {
      if (item.sale_price_gel && item.purchase_price_usd) {
        return sum + (item.sale_price_gel - item.purchase_price_usd * DEFAULT_USD_GEL_RATE);
      }
      return sum;
    }, 0);

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('პოპ-აპი დაბლოკილია. გთხოვთ ნებადართოთ.');
      return;
    }

    const rows = filteredItems.map(item => {
      const profit = item.sale_price_gel && item.purchase_price_usd
        ? (item.sale_price_gel - item.purchase_price_usd * DEFAULT_USD_GEL_RATE).toFixed(2)
        : '-';
      return `<tr>
        <td>${item.item_number || ''}</td>
        <td>${item.name || ''}</td>
        <td>${item.platform || ''}</td>
        <td>$${item.purchase_price_usd || 0}</td>
        <td>${item.sale_price_gel ? formatCurrency(item.sale_price_gel) : '-'}</td>
        <td>${profit !== '-' ? formatCurrency(parseFloat(profit)) : '-'}</td>
        <td>${translateStatus(item.status)}</td>
        <td>${item.client_name || '-'}</td>
        <td>${formatDate(item.order_date)}</td>
      </tr>`;
    }).join('');

    printWindow.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Deals Manager - ექსპორტი</title>
    <style>
      body { font-family: 'Segoe UI', Tahoma, sans-serif; padding: 30px; color: #333; }
      h1 { color: #e11d48; margin-bottom: 5px; }
      .subtitle { color: #888; margin-bottom: 20px; }
      table { width: 100%; border-collapse: collapse; font-size: 12px; }
      th { background: #f8f8f8; border: 1px solid #ddd; padding: 8px 10px; text-align: left; font-weight: 600; }
      td { border: 1px solid #ddd; padding: 6px 10px; }
      tr:nth-child(even) { background: #fafafa; }
      .summary { margin-top: 20px; padding: 15px; background: #f0fdf4; border-radius: 8px; border: 1px solid #bbf7d0; }
      .summary span { font-weight: bold; color: #16a34a; }
      @media print { body { padding: 10px; } }
    </style></head><body>
    <h1>🛍️ Deals Manager</h1>
    <div class="subtitle">ექსპორტის თარიღი: ${new Date().toLocaleDateString('ka-GE')}</div>
    <table><thead><tr>
      <th>ნომერი</th><th>სახელი</th><th>პლატფორმა</th><th>ფასი (USD)</th>
      <th>გაყიდვა (GEL)</th><th>მოგება</th><th>სტატუსი</th><th>კლიენტი</th><th>თარიღი</th>
    </tr></thead><tbody>${rows}</tbody></table>
    <div class="summary">
      სულ ნივთები: <span>${filteredItems.length}</span> | 
      ჯამური მოგება: <span>${formatCurrency(totalProfit)}</span>
    </div>
    <script>setTimeout(() => { window.print(); }, 300);</script>
    </body></html>`);
    printWindow.document.close();
    toast.success('PDF ექსპორტი მზადაა!');
  }, [filteredItems, toast]);

  const exportToExcel = () => {
    const { utils, writeFile } = XLSX;
    const data = filteredItems.map(item => ({
      'ნივთის ნომერი': item.item_number, 'სახელი': item.name, 'პლატფორმა': item.platform,
      'ლინკი': item.purchase_link, 'ტრეკინგი': item.tracking_code,
      'შეძენის ფასი (USD)': item.purchase_price_usd, 'გაყიდვის ფასი (GEL)': item.sale_price_gel,
      'მოგება (GEL)': item.sale_price_gel && item.purchase_price_usd
        ? (item.sale_price_gel - item.purchase_price_usd * DEFAULT_USD_GEL_RATE).toFixed(2) : '',
      'სტატუსი': translateStatus(item.status),
      'აქაუნთი': accounts.find(a => a.id === item.account_id)?.name || '-',
      'კლიენტი': item.client_name, 'საკონტაქტო': item.client_contact,
      'თარიღი': formatDate(item.order_date)
    }));
    const ws = utils.json_to_sheet(data);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'ნივთები');
    writeFile(wb, `Deals_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel ფაილი შენახულია!');
  };

  if (itemsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Package className="h-12 w-12 mx-auto mb-4 animate-spin text-primary" />
          <p className="text-muted-foreground">იტვირთება...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthWrapper loadingFallback={<AuthLoadingSkeleton />}>
      <div className="min-h-screen bg-background">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4
                     focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground
                     focus:rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          გადადით მთავარ კონტენტზე
        </a>

        <Header onAddItem={() => setIsAddDialogOpen(true)} onExport={exportToExcel} onExportPDF={exportToPdf} />

        <main id="main-content" className="container mx-auto px-4 py-6 pb-24 md:pb-6">
          <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
            {filteredItems?.length || 0} ნივთი ნაჩვენებია
          </div>

          <DashboardStats stats={stats} accountLimits={accountLimits} paymentStats={paymentStats} />
          <AnalyticsSummary items={allItems} />
          <AccountLimits accountLimits={accountLimits} />

          <Card className="mb-4">
            <CardContent className="pt-4">
              <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
            </CardContent>
          </Card>

          {activeTab === 'items' && (
            <>
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[200px]">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="search-input"
                          placeholder="ძიება..."
                          defaultValue={debouncedSearchTerm}
                          onChange={(e) => setDebouncedSearchTerm(e.target.value)}
                          className="pl-10"
                          aria-label="ნივთების ძიება"
                          aria-describedby="search-description"
                        />
                        <span id="search-description" className="sr-only">
                          ძიება ნივთის ნომრით, სახელით, ტრეკინგით ან კლიენტის სახელით
                        </span>
                      </div>
                    </div>
                    <div>
                      <Select
                        id="status-filter"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        aria-label="სტატუსის ფილტრი"
                      >
                        <option value="all">ყველა სტატუსი</option>
                        <option value="ordered">გამოწერილია</option>
                        <option value="at_carrier">გადამზიდავში</option>
                        <option value="shipped">გამოგზავნილია</option>
                        <option value="arrived">ჩამოტანილია</option>
                        <option value="at_home">სახლში/საწყობში</option>
                        <option value="delivered_to_client">გაცემული</option>
                      </Select>
                    </div>
                    <div>
                      <Select
                        id="platform-filter"
                        value={platformFilter}
                        onChange={(e) => setPlatformFilter(e.target.value)}
                        aria-label="პლატფორმის ფილტრი"
                      >
                        <option value="all">ყველა პლატფორმა</option>
                        {platforms.map(platform => (<option key={platform} value={platform}>{platform}</option>))}
                      </Select>
                    </div>
                    <div className="text-sm text-muted-foreground flex items-center" aria-live="polite">
                      ნაჩვენებია: {filteredItems?.length || 0} / {allItems?.length || 0}
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle>ნივთები</CardTitle></CardHeader>
                <CardContent>
                  {allItems?.length > 0 ? (
                    filteredItems?.length > 0 ? (
                      <>
                        <div className="md:hidden">
                          {filteredItems.map(item => (
                            <ItemCard
                              key={item.id}
                              item={item}
                              account={accounts.find(a => a.id === item.account_id)}
                              onNextStatus={(item) => handleNextStatus(item)}
                              onPay={(item) => setQuickPayItem(item)}
                              onEdit={(item) => handleEditItem(item)}
                              isSelected={selectedItems.has(item.id)}
                              onSelect={handleSelectItem}
                            />
                          ))}
                        </div>
                        <div className="hidden md:block">
                          <Suspense fallback={<ItemsTableSkeleton />}>
                            <ItemsTable 
                              items={filteredItems} 
                              accounts={accounts} 
                              onQuickPay={(item) => setQuickPayItem(item)}
                              selectedItems={selectedItems}
                              onSelectItem={handleSelectItem}
                              onSelectAll={handleSelectAll}
                            />
                          </Suspense>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">
                        <AlertCircle className="h-12 w-12 mx-auto mb-4" />
                        <p>ნივთები ვერ მოიძებნა</p>
                      </div>
                    )
                  ) : (
                    <EmptyItemState onAddItem={() => setIsAddDialogOpen(true)} />
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {activeTab === 'clients' && (
            <Suspense fallback={<ItemsTableSkeleton />}>
              <ClientsManager />
            </Suspense>
          )}

          {activeTab === 'companies' && (
            <Suspense fallback={<ItemsTableSkeleton />}>
              <CompaniesManager />
            </Suspense>
          )}

          {activeTab === 'accounts' && (
            <Suspense fallback={<ItemsTableSkeleton />}>
              <AccountsManager />
            </Suspense>
          )}
        </main>
        <div className="md:hidden">
          <BottomNav activeTab={activeTab} onTabChange={setActiveTab} />
          <FloatingActionButton onClick={() => setIsAddDialogOpen(true)} />
        </div>
        <Suspense fallback={<AddItemFormSkeleton />}>
          <AddItemForm 
            isOpen={isAddDialogOpen} 
            onClose={() => { setIsAddDialogOpen(false); setEditItem(null); }} 
            accounts={accounts}
            shippingCompanies={shippingCompanies}
            sourceCompanies={sourceCompanies}
            editItem={editItem}
          />
        </Suspense>
        <BulkActionsToolbar
          selectedCount={selectedItems.size}
          onDelete={handleBulkDelete}
          onStatusChange={handleBulkStatusChange}
          onClearSelection={handleClearSelection}
        />
        <AddPaymentForm
          isOpen={!!quickPayItem}
          onClose={() => setQuickPayItem(null)}
          itemId={quickPayItem?.id}
          itemData={quickPayItem}
        />
      </div>
    </AuthWrapper>
  );
}